import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A bounded undo stack with a self-expiring top entry.
 *
 * Replaces a single-slot undo that could hold one snapshot at a time. The
 * behaviour that single slot got right is kept, because it was not incidental:
 *
 *  - Snapshots taken in quick succession COALESCE. Clicking Shuffle while the
 *    Bulk Edit textarea is focused fires a blur (arming one snapshot) and then
 *    the shuffle (arming another) — treating those as two entries makes the
 *    first unreachable behind a second Undo nobody knows to press. Within the
 *    coalesce window the older snapshot is kept and only the label is widened.
 *  - The label must describe what Undo will actually DO. Once a window spans
 *    two different actions, naming only the latest one is a lie.
 *
 * Expiry applies to the TOP entry only. Older entries stay until they are
 * popped or pushed off the end — the timer exists so a stale toast disappears,
 * not to quietly discard history the user may still reach with Ctrl+Z.
 */

/** How long the newest snapshot stays offered before the toast retires it. */
export const UNDO_EXPIRY_MS = 8000;

/**
 * Snapshots arriving within this window are folded into the previous one.
 * Long enough to absorb a blur-then-click pair, short enough that two
 * deliberate actions stay separately undoable.
 */
const COALESCE_MS = 400;

export interface UndoEntry<T> {
  snapshot: T;
  label: string;
}

export interface UndoHistory<T> {
  /** Record the state *before* a destructive action. Call it before mutating. */
  pushSnapshot: (snapshot: T, label: string) => void;
  /** Restore the most recent snapshot, or null when there is nothing to undo. */
  undo: () => T | null;
  canUndo: boolean;
  /** Label of the snapshot Undo would apply, for the toast. */
  lastActionLabel: string | null;
  clearHistory: () => void;
  /** True while the newest snapshot is still within its expiry window. */
  isFresh: boolean;
}

export function useUndoHistory<T>(maxDepth = 10): UndoHistory<T> {
  // A ref, not state: pushing must not re-render, and the stack is read only
  // inside callbacks. `depth` below is the render-visible projection of it.
  const stack = useRef<UndoEntry<T>[]>([]);
  const lastPushAt = useRef(0);
  const expiryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [depth, setDepth] = useState(0);
  const [label, setLabel] = useState<string | null>(null);
  const [isFresh, setIsFresh] = useState(false);

  const clearExpiry = () => {
    if (expiryTimer.current) {
      clearTimeout(expiryTimer.current);
      expiryTimer.current = null;
    }
  };

  const pushSnapshot = useCallback((snapshot: T, actionLabel: string) => {
    const now = Date.now();
    const top = stack.current[stack.current.length - 1];
    const coalescing = top !== undefined && now - lastPushAt.current < COALESCE_MS;

    if (coalescing) {
      // Keep the OLDER snapshot — undo should rewind to before the burst
      // started, never to the middle of it.
      top.label = top.label === actionLabel ? actionLabel : 'Undo recent changes';
      setLabel(top.label);
    } else {
      stack.current.push({ snapshot, label: actionLabel });
      // Drop from the bottom once past maxDepth; the oldest history is the
      // least likely to be wanted and the most expensive to keep.
      if (stack.current.length > maxDepth) stack.current.shift();
      setLabel(actionLabel);
    }

    lastPushAt.current = now;
    setDepth(stack.current.length);
    setIsFresh(true);

    // Only the toast expires. The entry stays on the stack for Ctrl+Z.
    clearExpiry();
    expiryTimer.current = setTimeout(() => {
      expiryTimer.current = null;
      setIsFresh(false);
    }, UNDO_EXPIRY_MS);
  }, [maxDepth]);

  const undo = useCallback((): T | null => {
    const entry = stack.current.pop();
    if (!entry) return null;
    clearExpiry();
    setDepth(stack.current.length);
    const next = stack.current[stack.current.length - 1];
    setLabel(next?.label ?? null);
    setIsFresh(false);
    return entry.snapshot;
  }, []);

  const clearHistory = useCallback(() => {
    stack.current = [];
    clearExpiry();
    setDepth(0);
    setLabel(null);
    setIsFresh(false);
  }, []);

  // A pending timer firing after unmount would set state on a dead component.
  useEffect(() => clearExpiry, []);

  return {
    pushSnapshot,
    undo,
    canUndo: depth > 0,
    lastActionLabel: label,
    clearHistory,
    isFresh,
  };
}
